import { __extends } from "tslib";
import { SceneImage2D } from '@awayjs/scene';
import { MovieClip as AwayMovieClip } from '@awayjs/scene';
import { Sound } from '../media/Sound';
import { ASObject, Multiname, AXApplicationDomain } from '@awayfl/avm2';
/**
 * The ApplicationDomain class is a container for discrete groups of class definitions.
 * Application domains are used to partition classes that are in the same security domain.
 * They allow multiple definitions of the same class to exist and allow children to reuse parent
 * definitions.
 *
 *   <p class="- topic/p ">Application domains are used when an external SWF file is loaded through the Loader class.
 * All ActionScript 3.0 definitions in the loaded SWF file are stored in the application
 * domain, which is specified by the applicationDomain property of the LoaderContext
 * object that you pass as a context parameter of the Loader object's load() or
 * loadBytes() method. The LoaderInfo object also contains an
 * applicationDomain property, which is read-only.
 * All code in a SWF file is defined to exist in an application domain. The current application
 * domain is where your main application runs. The system domain contains all application domains,
 * including the current domain, which means that it contains all Flash Player classes.Every application domain,
 * except the system domain, has an associated parent domain.
 * The parent domain of your main application's application domain is the system domain.
 * Loaded classes are defined only when their parent doesn't already define them.
 * You cannot override a loaded class definition with a newer definition.
 * For usage examples of application domains, see the.
 * The ApplicationDomain() constructor function allows you to create an ApplicationDomain object.</p>
 *
 * @internal	Security considerations for application domains are discussed in the
 *   applicationDomain property entries of URLRequest and LoaderInfo.
 */
var ApplicationDomain = /** @class */ (function (_super) {
    __extends(ApplicationDomain, _super);
    /**
     * Creates a new application domain.
     * @param	parentDomain	If no parent domain is passed in,
     * this application domain takes the system domain as its parent.
     */
    function ApplicationDomain(parentDomain, isSystemDomain) {
        if (parentDomain === void 0) { parentDomain = null; }
        if (isSystemDomain === void 0) { isSystemDomain = false; }
        var _this = _super.call(this) || this;
        if (!isSystemDomain && parentDomain == null && ApplicationDomain._currentDomain != null) {
            //ApplicationDomain.currentDomain;
            parentDomain = ApplicationDomain.getSystemDomain();
        }
        if (!ApplicationDomain._currentDomain)
            ApplicationDomain._currentDomain = _this;
        _this._parentDomain = parentDomain;
        _this._definitions = {};
        _this._font_definitions = {};
        _this._audio_definitions = {};
        if (isSystemDomain) {
            _this.axApplicationDomain = _this.sec.system;
        }
        else if (!parentDomain) {
            _this.axApplicationDomain = _this.sec.application;
        }
        else {
            _this.axApplicationDomain = new AXApplicationDomain(_this.sec, _this.parentDomain.axApplicationDomain);
        }
        //@ts-ignore
        // store crossref back
        _this.axApplicationDomain.awayApplicationDomain = _this;
        return _this;
    }
    ApplicationDomain.getSystemDomain = function () {
        if (ApplicationDomain._systemDomain == null) {
            if (!ApplicationDomain._currentDomain) {
                return null;
            }
            ApplicationDomain._systemDomain =
                new this._currentDomain.sec.flash.system.ApplicationDomain(null, true);
        }
        return ApplicationDomain._systemDomain;
    };
    Object.defineProperty(ApplicationDomain, "currentDomain", {
        /**
         * Gets the current application domain in which your code is executing.
         * @internal	Question: Do you call System.currentDomain? or Loader.currentDomain or request.currentDomain?
         */
        get: function () {
            /*if(ApplicationDomain._systemDomain==null)
                ApplicationDomain._systemDomain=new ApplicationDomain();
            if(ApplicationDomain._currentDomain==null)
                ApplicationDomain._currentDomain=new ApplicationDomain(ApplicationDomain._systemDomain);*/
            return ApplicationDomain._currentDomain;
        },
        set: function (value) {
            ApplicationDomain._currentDomain = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ApplicationDomain.prototype, "domainMemory", {
        /**
         * Gets and sets the object on which domain-global memory operations
         * will operate within this ApplicationDomain.
         */
        get: function () {
            console.log('[UNSAFE IMPLEMENTATION!] domainMemory:flash/ApplicationDomain');
            return this._memory;
        },
        set: function (mem) {
            console.log('[UNSAFE IMPLEMENTATION!] domainMemory:flash/ApplicationDomain');
            // Missed types! ByteArray has buffer instead arraybuffer
            if (mem || this._memory.buffer !== mem.buffer) {
                this._memoryView = new DataView(mem.buffer);
            }
            this._memory = mem;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ApplicationDomain.prototype, "internal_memoryView", {
        /**
         * Internal DataView for using domainMemory in runtime
         */
        get: function () {
            return this._memoryView;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ApplicationDomain, "MIN_DOMAIN_MEMORY_LENGTH", {
        /**
         * Gets the minimum memory object length required to be used as
         * ApplicationDomain.domainMemory.
         */
        get: function () {
            console.log('MIN_DOMAIN_MEMORY_LENGTH not implemented yet in flash/ApplicationDomain');
            return 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ApplicationDomain.prototype, "parentDomain", {
        /**
         * Gets the parent domain of this application domain.
         */
        get: function () {
            return this._parentDomain;
        },
        enumerable: false,
        configurable: true
    });
    ApplicationDomain.prototype.addDefinition = function (name, asset) {
        this._definitions[name] = asset;
    };
    ApplicationDomain.prototype.addAudioDefinition = function (name, asset) {
        this._audio_definitions[name] = asset;
    };
    ApplicationDomain.prototype.addFontDefinition = function (name, asset) {
        this._font_definitions[name] = asset;
    };
    ApplicationDomain.prototype.hasSymbolForClass = function (className) {
        return this._definitions[className] || this._font_definitions[className] || this._audio_definitions[className];
    };
    ApplicationDomain.prototype.getSymbolAdaptee = function (className) {
        var symbol = this._definitions[className];
        if (symbol) {
            if (symbol.isAsset && symbol.isAsset(SceneImage2D) || !symbol.adapter) {
                return symbol;
            }
            var clone = symbol.adapter.clone();
            if (clone.adaptee.isAsset(AwayMovieClip)) {
                clone.adaptee.currentFrameIndex = 0;
            }
            return clone.adaptee;
        }
        else if (this._font_definitions[className]) {
            return this._font_definitions[className];
        }
        else if (this._audio_definitions[className]) {
            return this._audio_definitions[className];
        }
        return null;
    };
    ApplicationDomain.prototype.getSymbolDefinition = function (className) {
        var symbol = this._definitions[className];
        if (symbol) {
            if (symbol.isAsset && symbol.isAsset(SceneImage2D)) {
                return symbol;
            }
            var clone = symbol.adapter.clone();
            if (clone.adaptee.isAsset(AwayMovieClip)) {
                clone.adaptee.currentFrameIndex = 0;
            }
            return clone;
        }
        else if (this._font_definitions[className]) {
            return this._font_definitions[className];
        }
        else if (this._audio_definitions[className]) {
            var sound = new Sound();
            sound.adaptee = this._audio_definitions[className];
            //sound.adaptee.adapter=sound;
            return sound;
        }
    };
    /**
     * Gets a public definition from the specified application domain.
     * The definition can be that of a class, a namespace, or a function.
     * @param	name	The name of the definition.
     * @return	The object associated with the definition.
     * @internal	throws SecurityError The definition belongs to a domain to which
     *   the calling code does not have access.
     * @throws	ReferenceError No public definition exists with the
     *   specified name.
     */
    ApplicationDomain.prototype.getDefinition = function (name) {
        /**
         * @todo Cache a FromSimpleName
         */
        return this.axApplicationDomain.getClass(Multiname.FromSimpleName(name));
    };
    ApplicationDomain.prototype.getFontDefinition = function (name) {
        return this._font_definitions[name];
    };
    ApplicationDomain.prototype.getAwayJSAudio = function (name) {
        return this._audio_definitions[name];
    };
    ApplicationDomain.prototype.getAudioDefinition = function (name) {
        var sound = new Sound();
        sound.adaptee = this._audio_definitions[name];
        //sound.adaptee.adapter=sound;
        return sound;
    };
    ApplicationDomain.prototype.getQualifiedDefinitionNames = function () {
        var allDefinitionsnames = [];
        for (var key in this._definitions) {
            if (this._definitions.hasOwnProperty(key)) {
                allDefinitionsnames[allDefinitionsnames.length] = key;
            }
        }
        return allDefinitionsnames;
    };
    /**
     * Checks to see if a public definition exists within the specified application domain.
     * The definition can be that of a class, a namespace, or a function.
     * @param	name	The name of the definition.
     * @return	A value of true if the specified definition exists; otherwise, false.
     */
    ApplicationDomain.prototype.hasDefinition = function (name) {
        // Exception will thrown when definition not exist.
        try {
            // slow, because run lookup
            return !!this.getDefinition(name);
        }
        catch (e) {
            return false;
        }
    };
    ApplicationDomain.prototype.hasFontDefinition = function (name) {
        return this._font_definitions.hasOwnProperty(name);
    };
    ApplicationDomain.prototype.hasAudioDefinition = function (name) {
        return this._audio_definitions.hasOwnProperty(name);
    };
    return ApplicationDomain;
}(ASObject));
export { ApplicationDomain };
